import { useState, Fragment } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Alert, CircularProgress, Collapse, Link, Tooltip,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type { GtmParseResponse } from '../../../shared/gtmTypes.js';
import {
  rowsFromReport, buildGroups, buildEventPayload, classifyCustomerType,
  diagnose, evaluateRow, upgradeByElimination,
  type Row, type Group, type CustomerType, type ResultTone,
} from './gtm/logic.js';
import AddDomainDialog from './AddDomainDialog.js';

/* eslint-disable @typescript-eslint/no-explicit-any */

const BRANDS = [
  { label: 'mso-digital.de', url: 'https://www.mso-digital.de/' },
  { label: 'symplr.de', url: 'https://symplr.de/' },
  { label: 'orbit-data.io', url: 'https://orbit-data.io/de/' },
  { label: 'ambition-performance.de', url: 'https://ambition-performance.de/' },
];

const TOOL_META: Record<string, { name: string; chip: string; color: string }> = {
  googletagmanager: { name: 'Google Tag Manager', chip: 'GTM', color: '#246FDB' },
  google_analytics: { name: 'Google Analytics 4', chip: 'GA4', color: '#E37400' },
  google_ads: { name: 'Google Ads', chip: 'ADS', color: '#4285F4' },
  doubleclick: { name: 'DoubleClick (Google Marketing)', chip: 'DC', color: '#34A853' },
  adservice: { name: 'Google AdServices', chip: 'ADS', color: '#4285F4' },
  bing: { name: 'Microsoft Ads (UET)', chip: 'MS', color: '#00809D' },
  facebook_pixel: { name: 'Meta Pixel', chip: 'META', color: '#0866FF' },
  linkedin: { name: 'LinkedIn Insight', chip: 'IN', color: '#0A66C2' },
  tiktok: { name: 'TikTok Pixel', chip: 'TT', color: '#69C9D0' },
  hotjar: { name: 'Hotjar', chip: 'HJ', color: '#FF3C00' },
  clarity: { name: 'Microsoft Clarity', chip: 'CL', color: '#00A4EF' },
  criteo: { name: 'Criteo', chip: 'CR', color: '#F45B00' },
  snapchat: { name: 'Snapchat Pixel', chip: 'SC', color: '#C9B400' },
  pinterest: { name: 'Pinterest Tag', chip: 'PIN', color: '#E60023' },
  matomo: { name: 'Matomo', chip: 'MTM', color: '#3450A3' },
  segment: { name: 'Segment CDP', chip: 'SEG', color: '#52BD95' },
  mixpanel: { name: 'Mixpanel', chip: 'MIX', color: '#7856FF' },
  hubspot: { name: 'HubSpot', chip: 'HUB', color: '#FF7A59' },
};
const AD_TOOLS = new Set(['google_ads', 'doubleclick', 'adservice', 'bing', 'facebook_pixel', 'linkedin', 'tiktok', 'criteo', 'snapchat', 'pinterest']);

const toneColor: Record<ResultTone, string> = { ok: colors.scoreGreen, warn: colors.scoreYellow, bad: colors.scoreRed };
function ToneIcon({ tone }: { tone: ResultTone }) {
  const sx = { fontSize: 18, color: toneColor[tone], verticalAlign: 'middle', mr: 0.5 };
  if (tone === 'ok') return <CheckCircleIcon sx={sx} />;
  if (tone === 'warn') return <WarningAmberIcon sx={sx} />;
  return <CancelIcon sx={sx} />;
}

function ToolList({ tools, markAdTools }: { tools?: string[]; markAdTools?: boolean }) {
  if (!tools || !tools.length) return <Typography variant="caption" sx={{ color: colors.black60 }}>keine beobachtet</Typography>;
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {tools.map((t) => {
        const m = TOOL_META[t] || { name: t, chip: t.slice(0, 4).toUpperCase(), color: '#5a6672' };
        const warn = markAdTools && AD_TOOLS.has(t);
        return (
          <Box component="li" key={t} sx={{ display: 'flex', alignItems: 'center', my: 0.4 }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 38, height: 19, borderRadius: '4px', fontSize: 10, fontWeight: 800, color: '#fff', mr: 1.1, px: 0.6, bgcolor: m.color, fontFamily: "'JetBrains Mono', monospace" }}>{m.chip}</Box>
            <Typography variant="body2" component="span">{m.name}</Typography>
            {warn && <Typography variant="caption" sx={{ color: colors.scoreRed, ml: 1, fontWeight: 600 }}>⚠ Werbe-Tool trotz Ablehnung aktiv</Typography>}
          </Box>
        );
      })}
    </Box>
  );
}

// Aufklappbare Zelle fuer Zusatzbedingungen / Detail-Listen
function Expandable({ summary, color, children }: { summary: React.ReactNode; color?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Box onClick={() => setOpen((o) => !o)} sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: color || colors.scoreYellow, fontWeight: 600, fontSize: 13 }}>
        <ExpandMoreIcon sx={{ fontSize: 18, transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
        {summary}
      </Box>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
}

function CondCell({ row }: { row: Row }) {
  if (!row.kontext.length) return <Typography variant="caption" sx={{ color: colors.black60 }}>keine</Typography>;
  return (
    <Expandable summary={<span>{row.kontext.length} Bedingung(en)</span>}>
      <Box component="ul" sx={{ m: '4px 0 0', pl: 2.2, color: colors.black60 }}>
        {row.kontext.map((k, i) => (
          <li key={i}><Typography variant="caption" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{k}</Typography></li>
        ))}
      </Box>
    </Expandable>
  );
}

function ResultCell({ row }: { row: Row }) {
  const r = row.result;
  if (!row._running && !r) return <Typography variant="caption" sx={{ color: colors.black60 }}>ungetestet</Typography>;
  if (row._running) return <Typography variant="caption" sx={{ color: colors.scoreYellow, fontWeight: 600 }}>⟳ wird getestet …</Typography>;
  if (!r) return null;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ToneIcon tone={r.tone} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: toneColor[r.tone] }}>{r.label}</Typography>
      </Box>
      <Box component="ul" sx={{ m: '4px 0 0', pl: 2.2, color: colors.black60 }}>
        {r.details.map((d, i) => <li key={i}><Typography variant="caption">{d}</Typography></li>)}
      </Box>
    </Box>
  );
}

// Kenn-Zelle: gesendeter Event-Name vs. aufgeloester Name aus dem Request
function KennCell({ row }: { row: Row }) {
  const resolved = row.resolvedFromRequest;
  const showResolved = resolved && (row.kennName.startsWith('{{') || ['custom', 'standard', '—'].includes(row.kennName));
  const gesendetHinweis = !row.label && row.kennName && row.kennName !== '—' && !row.kennName.startsWith('{{') && row.kennName !== row.event;
  if (showResolved) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{resolved}</Typography>
        <Typography variant="caption" sx={{ color: colors.black60 }}>aus Request aufgelöst · im Container: {row.kennName}</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.kennName}</Typography>
      {gesendetHinweis && <Typography variant="caption" sx={{ color: colors.black60 }}>gesendeter Event-Name · ausgelöst durch „{row.event}"</Typography>}
    </Box>
  );
}

interface GroupStatus { tone: ResultTone | 'mut'; text: string; running: boolean }

// Scan-Infobox: wichtigste Erkenntnisse aus dem n8n-Output des letzten Laufs
function ScanInfoBox({ run, eventName }: { run: any; eventName: string }) {
  const acc = run?.consent_accepted || {};
  const cmp = run?.cmp_data || {};
  const como = acc.consentModeDetection || {};
  const scr = acc.screening || {};
  const den = run?.consent_denied?.screening;

  // Server-Side-Tagging-Heuristik
  const allUrls: string[] = [];
  (acc.eventCheck?.events?.[0]?.triggeredRequests || []).forEach((x: any) => allUrls.push(x.url));
  (acc.consentModeDetection?.signals?.consentSignalsFromNetwork || []).forEach((x: any) => allUrls.push(x.url));
  let sgtmHost: string | null = null;
  for (const u of allUrls) {
    try {
      const url = new URL(u);
      const isCollect = /\/(g|mp)\/collect/.test(url.pathname);
      const isGoogleHost = /(^|\.)google-analytics\.com$|(^|\.)analytics\.google\.com$/.test(url.hostname);
      if (isCollect && !isGoogleHost) { sgtmHost = url.hostname; break; }
    } catch { /* ignore */ }
  }

  const rows: { label: string; value: React.ReactNode }[] = [];
  rows.push({ label: 'CMP erkannt', value: cmp.cmpName ? <span><b>{cmp.cmpName}</b> · Konfidenz: {cmp.confidence || '?'}</span> : <span style={{ color: colors.black60 }}>keine CMP-Analyse in diesem Run</span> });
  if (cmp.primarySignal) rows.push({ label: 'CMP-Hauptsignal', value: <span style={{ color: colors.black60 }}>{String(cmp.primarySignal).slice(0, 150)}</span> });
  if (cmp.tcf) rows.push({ label: 'IAB TCF', value: cmp.tcf.detected ? `erkannt · cmpId: ${cmp.tcf.cmpId ?? 'unbekannt'}` : 'nicht erkannt' });
  rows.push({ label: 'Consent Mode', value: <span><b>{como.consentMode || 'unbekannt'}</b>{como.consentModeDetail ? <span style={{ color: colors.black60 }}> · {como.consentModeDetail}</span> : null}</span> });
  rows.push({ label: 'Server-Side Tagging', value: sgtmHost ? <span style={{ color: colors.scoreYellow, fontWeight: 600 }}>wahrscheinlich vorhanden · First-Party-Endpoint „{sgtmHost}"</span> : <span style={{ color: colors.black60 }}>nicht erkannt · Requests gehen direkt an die Tool-Domains</span> });
  rows.push({ label: 'Consent-Klick', value: <span>{scr.cmp_button_clicked ? <span style={{ color: colors.scoreGreen, fontWeight: 700 }}>✓ geklickt</span> : <span style={{ color: colors.scoreRed, fontWeight: 700 }}>✗ fehlgeschlagen</span>} · Methode: {scr.cmp_click_method || '?'}{scr.cmp_clicked_buttons?.[0]?.text ? ` · Button: „${scr.cmp_clicked_buttons[0].text}"` : ''}</span> });
  if (scr.screening_output_consent_accepted) rows.push({ label: 'Tools nach Accept', value: <ToolList tools={scr.screening_output_consent_accepted} /> });
  if (den?.screening_output_consent_denied) rows.push({ label: 'Tools nach Deny', value: <ToolList tools={den.screening_output_consent_denied} markAdTools /> });
  if (acc.spaExploration?.visitedPaths?.length) rows.push({ label: 'SPA-Exploration', value: <span style={{ color: colors.black60 }}>{acc.spaExploration.visitedPaths.join(' · ')}</span> });
  rows.push({ label: 'Scanner-Version', value: acc.version || '?' });

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>Scan-Infos zum letzten Lauf <Typography component="span" variant="body2" sx={{ color: colors.black60 }}>· Event: {eventName}</Typography></Typography>
      <Table size="small">
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ width: 220, color: colors.black60, verticalAlign: 'top', border: 0, py: 0.6 }}>{r.label}</TableCell>
              <TableCell sx={{ border: 0, py: 0.6 }}>{r.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function WatchdogGtmAnalystPage() {
  const [domain, setDomain] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parse, setParse] = useState<GtmParseResponse | null>(null);
  const [customerType, setCustomerType] = useState<CustomerType | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [restRows, setRestRows] = useState<Row[]>([]);
  const [status, setStatus] = useState<Record<string, GroupStatus>>({});
  const [scanInfo, setScanInfo] = useState<{ run: any; eventName: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = () => { setGroups((gs) => [...gs]); };

  async function doParse(target?: string) {
    const d = (target ?? domain).trim();
    if (!d) return;
    if (target) setDomain(target);
    setParsing(true); setError(null);
    try {
      const res = await api.post<GtmParseResponse>('/gtm/parse', { domain: d });
      if (!res.success) throw new Error(res.error.message);
      const rows = rowsFromReport(res.data.report);
      setParse(res.data);
      setCustomerType(classifyCustomerType(res.data.report));
      setGroups(buildGroups(rows));
      setRestRows(rows.filter((r) => r.method !== 'dataLayer_push'));
      setStatus({});
      setScanInfo(null);
    } catch (e) {
      setError((e as Error).message);
      setParse(null); setGroups([]); setRestRows([]);
    }
    setParsing(false);
  }

  async function testEvent(event: string) {
    if (!parse) return;
    const g = groups.find((x) => x.event === event);
    if (!g) return;
    setBusy(true);
    setStatus((s) => ({ ...s, [event]: { tone: 'mut', text: 'SCAN LÄUFT … (~90–120 s)', running: true } }));
    g.rows.forEach((r) => { r._running = true; r.result = null; });
    refresh();
    try {
      const res = await api.post<any>('/gtm/simulate', { url: parse.pageUrl, event_name: event, event_payload: buildEventPayload(event) });
      const out = res.success ? res.data : { ok: false, error: res.error.message };
      g.rows.forEach((r) => { r._running = false; });
      if (!out.ok) {
        setStatus((s) => ({ ...s, [event]: { tone: 'bad', text: 'Scan-Problem: ' + (out.error || 'unbekannt') + (out.rawPreview ? ' · Antwort begann mit: „' + String(out.rawPreview).slice(0, 140) + '"' : ''), running: false } }));
        g.rows.forEach((r) => { r.result = { tone: 'warn', label: 'Scan-Problem: nicht bewertbar', details: [] }; });
        refresh(); setBusy(false); return;
      }
      const run = out.data;
      const d = diagnose(run);
      setStatus((s) => ({ ...s, [event]: { tone: d.tone, text: d.text, running: false } }));
      if (d.valid) setScanInfo({ run, eventName: event });
      g.rows.forEach((r) => { r.result = d.valid ? evaluateRow(r, d, parse.pageUrl) : { tone: 'warn', label: 'Scan-Problem: nicht bewertbar', details: [] }; });
      if (d.valid) upgradeByElimination(g);
      refresh();
    } catch (e) {
      g.rows.forEach((r) => { r._running = false; r.result = null; });
      setStatus((s) => ({ ...s, [event]: { tone: 'bad', text: 'Fehler: ' + (e as Error).message, running: false } }));
      refresh();
    }
    setBusy(false);
  }

  async function testAll() {
    for (const g of groups) await testEvent(g.event); // sequenziell
  }

  function exportMd() {
    if (!parse) return;
    const strip = (r: Row['result']) => (r ? r.label + (r.details.length ? ' · ' + r.details.join(' · ') : '') : 'ungetestet');
    const lines: string[] = [
      '# Tracking-Scanner GTM-Analyst · Report', '',
      `- Domain: ${parse.pageUrl}`, `- Container: ${parse.usedId} (Version ${parse.report.container.version})`,
      `- Kundentyp (heuristisch): ${customerType?.typ}`, `- Erstellt: ${new Date().toISOString()}`, '',
      '## Direkt getestete Events', '',
    ];
    for (const g of groups) {
      lines.push(`### Event: \`${g.event}\``, `Scan-Diagnose: ${status[g.event]?.text || 'noch kein Lauf'}`, '',
        '| Kanal | Conversion-ID | Label/Event | Bedingungen | Ergebnis |', '|---|---|---|---|---|',
        ...g.rows.map((r) => `| ${r.tool} | \`${r.convId}\` | \`${r.kennName}\` | ${r.kontext.length ? r.kontext.join('; ') : '—'} | ${strip(r.result)} |`), '');
    }
    if (restRows.length) {
      lines.push('## Nicht automatisiert testbar', '', '| Kanal | Conversion-ID | Label/Event | Auslösendes Event | Methode |', '|---|---|---|---|---|',
        ...restRows.map((r) => `| ${r.tool} | \`${r.convId}\` | \`${r.kennName}\` | \`${r.event || '—'}\` | ${r.method} |`), '');
    }
    lines.push('---', '✅ = Request beobachtet und geblockt (kein Bot-Traffic) · ❌ = kein Request · nur bei „Scan ok" aussagekräftig.');
    const host = new URL(parse.pageUrl).hostname;
    const t = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}_${pad(t.getHours())}-${pad(t.getMinutes())}`;
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gtm-analyst_${host}_${parse.usedId}_${stamp}.md`;
    a.click();
  }

  const testableCount = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>GTM Analyst</Typography>
          <Typography variant="body2" sx={{ mb: 3, color: colors.black60 }}>
            Domain → GTM-Container parsen → Conversion-Inventar → Event-Simulation. Requests werden beobachtet, dann
            geblockt — es kommen keine Daten in echten Systemen an.
          </Typography>
        </Box>
        <Button
          variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={() => setAddOpen(true)}
          sx={{ borderColor: colors.silver, color: colors.black85 }}
        >
          Domain hinzufügen
        </Button>
      </Box>

      {/* ── Eingabe ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Domain, z. B. www.mso-digital.de" value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doParse(); }}
            sx={{ minWidth: 340 }}
          />
          <Button
            variant="contained" startIcon={parsing ? <CircularProgress size={16} sx={{ color: colors.black }} /> : <TravelExploreIcon />}
            onClick={() => doParse()} disabled={parsing || !domain.trim()}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey } }}
          >
            GTM analysieren
          </Button>
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: colors.black60 }}>Schnelltest unserer Brands:</Typography>
          {BRANDS.map((b) => (
            <Button key={b.url} size="small" variant="outlined" disabled={parsing}
              onClick={() => doParse(b.url)}
              sx={{ borderColor: colors.silver, color: colors.black85, textTransform: 'none' }}>
              {b.label}
            </Button>
          ))}
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {parse && customerType && (
          <Box sx={{ mt: 2, color: colors.black85 }}>
            <Typography variant="body2">
              Container: <b>{parse.usedId}</b> (Version {parse.report.container.version}) · {parse.report.tags.length} Tags gesamt ·{' '}
              {groups.length + restRows.length > 0 ? `${groups.reduce((n, g) => n + g.rows.length, 0) + restRows.length} Conversion-Tags` : '0 Conversion-Tags'}, davon <b>{testableCount} direkt testbar</b> über {groups.length} Events
              {parse.gtmIds.length > 1 && ` · weitere IDs auf der Seite: ${parse.gtmIds.slice(1).join(', ')}`}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: colors.black60 }}>Kundentyp (heuristisch):</Typography>
              <Chip label={customerType.typ} size="small" sx={{ bgcolor: customerType.farbe + '22', color: customerType.farbe, border: `1px solid ${customerType.farbe}66`, fontWeight: 700 }} />
            </Box>
            {(customerType.lead.length > 0 || customerType.ecom.length > 0) && (
              <Typography variant="caption" sx={{ color: colors.black60, display: 'block', mt: 0.5 }}>
                {customerType.lead.length > 0 && <>Lead-Signale: {customerType.lead.slice(0, 6).join(', ')}{customerType.lead.length > 6 ? ' …' : ''}</>}
                {customerType.lead.length > 0 && customerType.ecom.length > 0 && <br />}
                {customerType.ecom.length > 0 && <>eCommerce-Signale: {customerType.ecom.slice(0, 6).join(', ')}{customerType.ecom.length > 6 ? ' …' : ''}</>}
              </Typography>
            )}
            {/* Andockpunkt aus dem PRD: aus der Analyse direkt einen Kunden-Datensatz machen. */}
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.silver}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setAddOpen(true)}
                sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}
              >
                Als Domain anlegen
              </Button>
              <Typography variant="caption" sx={{ color: colors.black60 }}>
                Übernimmt Container und Scanprofil in die Watchdog-Konfiguration.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {scanInfo && <ScanInfoBox run={scanInfo.run} eventName={scanInfo.eventName} />}

      {/* ── Direkt testbare Conversions ── */}
      {groups.length > 0 && (
        <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Direkt testbare Conversions <Typography component="span" variant="body2" sx={{ color: colors.black60 }}>(Custom Events → dataLayer-Push)</Typography></Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={testAll} disabled={busy}
                sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}>Alle Events testen</Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportMd}
                sx={{ borderColor: colors.silver, color: colors.black85 }}>Export als Markdown</Button>
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.lightGrey }}>
                <TableCell sx={{ width: '15%' }}>Kanal</TableCell>
                <TableCell sx={{ width: '16%' }}>Conversion-ID</TableCell>
                <TableCell sx={{ width: '21%' }}>Label / Event-Name</TableCell>
                <TableCell sx={{ width: '20%' }}>Zusatzbedingungen</TableCell>
                <TableCell sx={{ width: '28%' }}>Ergebnis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const st = status[g.event];
                const payload = buildEventPayload(g.event);
                let dividerDone = false;
                return (
                  <Fragment key={g.event}>
                    <TableRow sx={{ bgcolor: st?.running ? '#fff7e6' : '#f0f2f5' }}>
                      <TableCell colSpan={5} sx={{ borderTop: `2px solid ${colors.silver}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography component="span" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{g.event}</Typography>
                          <Button size="small" variant="outlined" onClick={() => testEvent(g.event)} disabled={busy}
                            sx={{ py: 0.2, minWidth: 0, borderColor: colors.silver, color: colors.black85 }}>Test</Button>
                          {payload && (
                            <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 11 }}>{JSON.stringify(payload, null, 2)}</pre>}>
                              <Typography variant="caption" sx={{ color: colors.black60, cursor: 'help' }}>🛒 eCommerce-Testdaten werden mitgesendet</Typography>
                            </Tooltip>
                          )}
                          <Typography component="span" variant="caption" sx={{ color: st ? (st.tone === 'bad' ? colors.scoreRed : st.tone === 'ok' ? colors.scoreGreen : colors.black60) : colors.black60, fontWeight: st?.running ? 700 : 400 }}>
                            {st?.running && <CircularProgress size={12} sx={{ mr: 0.5, color: colors.scoreYellow }} />}
                            {st?.text || 'noch kein Lauf'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((r, ri) => {
                      const showDivider = !dividerDone && r.kontext.length > 0;
                      if (showDivider) dividerDone = true;
                      return (
                        <Fragment key={`${g.event}_${ri}`}>
                          {showDivider && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ bgcolor: '#fafbfc', color: colors.black60, fontSize: 11.5, py: 0.5 }}>
                                ↓ mit Zusatzbedingungen · feuern nur, wenn die Bedingung im Run erfüllt ist
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell sx={{ verticalAlign: 'top' }}>{r.tool}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top', fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{r.convId}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}><KennCell row={r} /></TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}><CondCell row={r} /></TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}><ResultCell row={r} /></TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Box sx={{ p: 2, color: colors.black60, fontSize: 13, lineHeight: 1.7 }}>
            <div><b style={{ color: colors.scoreGreen }}>✅ Request ausgelöst</b> · Kennung dieses Tags im Request nachgewiesen. Request wurde beobachtet, dann geblockt.</div>
            <div><b style={{ color: colors.scoreYellow }}>～ wahrscheinlich ausgelöst</b> · Kanal-Request beobachtet, tag-genaue Kennung nicht verfügbar, keine widersprechenden Bedingungen.</div>
            <div><b style={{ color: colors.scoreRed }}>❌ vermutlich nicht gefeuert</b> · Kanal-Request kam, aber die Bedingung DIESES Tags war nicht erfüllt.</div>
            <div><b style={{ color: colors.scoreRed }}>❌ kein Request beobachtet</b> · gar kein Request dieses Kanals. Nur aussagekräftig bei „Scan ok".</div>
          </Box>
        </Paper>
      )}

      {/* ── Weitere Conversion-Tags ── */}
      {restRows.length > 0 && (
        <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Weitere Conversion-Tags <Typography component="span" variant="body2" sx={{ color: colors.black60 }}>(noch nicht automatisiert testbar)</Typography></Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.lightGrey }}>
                <TableCell sx={{ width: '15%' }}>Kanal</TableCell>
                <TableCell sx={{ width: '16%' }}>Conversion-ID</TableCell>
                <TableCell sx={{ width: '21%' }}>Label / Event-Name</TableCell>
                <TableCell sx={{ width: '20%' }}>Auslösendes Event</TableCell>
                <TableCell sx={{ width: '28%' }}>Was fehlt zum Test?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {restRows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ verticalAlign: 'top' }}>{r.tool}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top', fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{r.convId}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top', fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{r.kennName}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {r.event ? <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.event}</Typography> : '—'}
                    <Box sx={{ mt: 0.4 }}><CondCell row={r} /></Box>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {r.method === 'navigation'
                      ? <Chip label="navigation" size="small" sx={{ bgcolor: '#fff3d6', color: '#a06800', fontWeight: 600 }} />
                      : <Chip label="synthetischer_push" size="small" sx={{ bgcolor: '#ffe6de', color: '#a03a1a', fontWeight: 600 }} />}
                    <Typography variant="caption" sx={{ display: 'block', color: colors.black60, mt: 0.5 }}>
                      {r.method === 'navigation' ? 'Test = Aufruf der Ziel-URL statt Event-Push' : 'Braucht Push mit gtm.*-Feldern (kommt mit der event_check-Erweiterung)'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddDomainDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        initialHostname={parse?.pageUrl ?? domain}
      />
    </Box>
  );
}
